/**
 * Halcy marketing design system primitives (DESIGN_SYSTEM.md § Part 1).
 *
 * This directory is the implementation home for shared marketing UI; the
 * supported usage, states and the migration path off `components/elements`
 * are in `apps/landing/README.md` § Design System.
 *
 * `MarketingSurface` wraps a page and is the only place the tokens are
 * declared. The rest are the shared rules every redesigned section reads:
 * layout (`Section`, `Rail`), type roles (`Eyebrow`, plus the `display`,
 * `lead`, `body`, `support` and `caption` classes on `marketingStyles`), and
 * the two interactive patterns (`Button`, `TextLink`).
 *
 * The visual primitives import no analytics or modal context. The booking
 * action lives in its own hook (`useBookingCta`, and its `useTalkToUs` form)
 * so a page can pair it with the shared `Button` or with a skin of its own.
 * The FAQ answer-height transition is `useDisclosureMotion`, which animates
 * a list of native `<details>` rows and hands the expansion back through
 * `onExpand` so the analytics call stays in the page as well. `Faq` is the
 * section built on it – heading, conversation card and accordion in the
 * homepage design – and takes its CTA control and `onExpand` from the page
 * for the same reason.
 *
 * A section module owns only what is its own: its grid, its cards, its
 * diagrams. Anything two sections would both need belongs here.
 */
export { MarketingSurface } from './MarketingSurface';
export { Section } from './Section';
export { Rail } from './Rail';
export { Eyebrow } from './Eyebrow';
export { Button, type ButtonProps } from './Button';
export { TextLink } from './TextLink';
export { LinkedText } from './LinkedText';
export { useBookingCta } from './useBookingCta';
export { useTalkToUs } from './useTalkToUs';
export { useDisclosureMotion, type DisclosureMotionOptions } from './useDisclosureMotion';
export { Faq, type FaqCard, type FaqItem, type FaqProps } from './Faq';
export { default as marketingStyles } from './marketing.module.css';

export { ReadingLayout } from './ReadingLayout';
export { JumpNavigation } from './JumpNavigation';
export { SourcesPanel } from './SourcesPanel';
export { SourceChart, SourceChartRow, StatValue } from './SourceChart';
export { InteriorPage } from './InteriorPage';
export { SiteHeader } from './SiteHeader';
export { Breadcrumb, type BreadcrumbCrumb } from './Breadcrumb';
export { default as interiorStyles } from './interior.module.css';
export { CompactScan } from './CompactScan';

export { RelatedResources, ResourceDescription } from './RelatedResources';
